<template>
  <div class="image-details">
    <MaterialTextArea
      v-model="source.url"
      v-bind="formFields.url"
      className="image-inspector-input"
    />
    <MaterialInput
      v-if="!source.canDelete"
      v-model="assetTypeRole"
      label="Asset Role"
      className="image-inspector-input"
      :disabled="true"
    />
    <MaterialSelect
      v-else
      v-model="formData.assetRole"
      v-bind="formFields.assetRole"
      :options="assetRolesOptions"
      :disabled="isEmptyAssetOptions"
      className="image-inspector-select"
    />

    <MaterialSelect
      v-if="source.canDelete"
      v-model="formData.language"
      v-bind="formFields.language"
      :options="languages"
      className="image-inspector-select"
    />

    <div class="info-title">
      File Information:
    </div>
    <div class="info-item">
      <div class="item-title">
        Title:
      </div>
      <div class="item-description">
        {{ source.name || '-' }}
      </div>
    </div>
    <div class="info-item">
      <div class="item-title">
        Type:
      </div>
      <div class="item-description">
        {{ source.type }}
      </div>
    </div>
    <div
      v-if="source.format"
      class="info-items"
    >
      <div class="info-item">
        <div class="item-title">
          Format:
        </div>
        <div :class="['item-description']">
          {{ source.format || '-' }}
        </div>
      </div>
    </div>
    <div
      v-if="source.dimensions"
      class="info-items"
    >
      <div class="info-item">
        <div class="item-title">
          Dimension:
        </div>
        <div class="item-description">
          {{ source.dimensions }}
        </div>
      </div>
    </div>
    <div class="info-items">
      <div class="info-item">
        <div class="item-title">
          File Size:
        </div>
        <div
          v-if="source.filesize"
          class="item-description"
        >
          {{ source.filesize | fileSize }}
        </div>
        <div
          v-else
          class="item-description"
        >
          -
        </div>
      </div>
    </div>

    <div
      v-if="source.canDelete"
      class="btn-wrapper"
    >
      <Button
        title="Delete"
        classType="transparent"
        :eventFunction="onOpenDeleteModalHandle"
        :disabled="source.status === 'processing'
          || !source.canDelete"
      />
      <Button
        title="Revert"
        classType="warning"
        :eventFunction="onRevertChangesHandle"
        :disabled="!hasChanges"
      />
      <Button
        title="Apply"
        classType="primary"
        className="apply-btn"
        :eventFunction="onEditImageAsset"
        :disabled="!hasChanges"
      />
    </div>
    <NotificationModal
      ref="modal"
      v-model="isModalRemoveOpen"
      :modal="isModalRemoveOpen"
      :bodyMessage="bodyMessageDeleteModal"
      title="Delete Image"
      :onAccept="removeImageAsset"
      :onClose="onCloseRemove"
      :isOrangeCancel="true"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { SET_EDIT_FORM } from '../../../store/actions/common';
import Button from '../../common/Button.vue';
import MaterialTextArea from '../../inputs/MaterialTextArea.vue';
import MaterialInput from '../../inputs/MaterialInput.vue';
import MaterialSelect from '../../inputs/MaterialSelect.vue';
import NotificationModal from '../../NotificationModal.vue';
import BaseForm from '../../forms/BaseForm.vue';
import {
  DELETE_IMAGE_ASSET_REQUEST,
  EDIT_IMAGE_ASSET_REQUEST,
  GET_ASSET_ROLES_OPTIONS_REQUEST,
} from '../../../store/actions/media/mediaAsset';

export default {
  name: 'AssetImageInspector',
  components: {
    MaterialSelect,
    MaterialInput,
    Button,
    MaterialTextArea,
    NotificationModal,
  },
  extends: BaseForm,
  props: {
    source: {
      type: Object,
      default: () => {},
    },
    getMediaItemAssets: {
      type: Function,
      default: () => {},
    },
    setAssetIsInProcessing: {
      type: Function,
      default: () => {},
    },
    addAssetItemWithChanges: {
      type: Function,
      default: () => {},
    },
    removeAssetItemWithChanges: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      assetTypeReq: 'Image',
      error: false,
      isModalRemoveOpen: false,
      bodyMessageDeleteModal: '',
      assetRolesOptions: [],
      assetTypeRole: '',
      formatError: '',
      formData: {
        language: this.source.language,
        assetRole: this.source.assetRole && this.source.assetRole?.id,
      },
      formFields: {
        url: {
          name: 'url',
          label: 'Link URL',
          disabled: true,
          cols: 30,
          rows: 10,
          allowCopy: true,
        },
        language: {
          name: 'language',
          formPrefix: this.formPrefix,
          rules: {
            required: this.source.type === 'alternate audio',
          },
        },
        assetRole: {
          type: 'text',
          name: 'Asset Role',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
          },
        },
      },
      cachedFormData: {
        language: this.source.language,
        assetRole: this.source.assetRole && this.source.assetRole?.id,
      },
    };
  },
  computed: {
    ...mapState({
      mediaItem: (state) => state.mediaItem.mediaItem,
      languages: (state) => state.mediaAsset.languageList,
      isLoading: (state) => state.mediaAsset.isLoading,
    }),
    ...mapGetters([
      'getCurrentAccountId',
    ]),
    hasChanges() {
      return this.formData.language !== this.cachedFormData.language
        || this.formData.assetRole !== this.cachedFormData.assetRole;
    },
    isEmptyAssetOptions() {
      return this.assetRolesOptions.length === 0 || this.assetRolesOptions[0].code === 'No-items';
    },
  },
  watch: {
    hasChanges: {
      handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return;
        }

        if (newVal) {
          this.addAssetItemWithChanges(this.source.id);
        } else {
          this.removeAssetItemWithChanges(this.source.id);
        }
      },
      immediate: false,
    },
  },
  created() {
    this.cachedFormData.assetRole = this.source.assetRole?.id;
    this.cachedFormData.language = this.source.language;

    if (!this.source.canDelete) {
      this.assetTypeRole = this.source.assetRole && this.source.assetRole.name;
      return;
    }

    const assetRolesParams = {
      accountId: this.getCurrentAccountId,
      mediaId: this.$route.params.mediaId,
      assetType: this.assetTypeReq,
    };
    this.$store.dispatch(GET_ASSET_ROLES_OPTIONS_REQUEST, assetRolesParams).then((res) => {
      this.assetRolesOptions = res.data.map((item) => ({
        code: item.id,
        name: item.name,
        multiAsset: item.multiAsset,
        uniqueAssetType: item.uniqueAssetType,
        enabled: item.enabled,
        usedAssetId: item.usedAssetId,
        usedAssetType: item.usedAssetType,
      }));
      this.assetRolesOptions = this.assetRolesOptions
        .filter((item) => item.enabled
          || (this.source.assetRole && this.source.assetRole.id === item.code));

      if (!this.assetRolesOptions.length) {
        this.assetRolesOptions = [{
          code: 'No-items',
          name: 'No items to display',
        }];

        this.formData.assetRole = this.assetRolesOptions[0].code;
      }
    });
  },
  beforeDestroy() {
    this.removeAssetItemWithChanges(this.source.id);
  },
  methods: {
    startProcessing() {
      this.setAssetIsInProcessing(this.source.id);
    },
    stopProcessing() {
      this.setAssetIsInProcessing(null);
    },
    onOpenDeleteModalHandle() {
      this.isModalRemoveOpen = true;
      this.bodyMessageDeleteModal = 'Are you sure you want to delete this Image?';
    },
    onCloseRemove() {
      this.isModalRemoveOpen = false;
      this.$store.dispatch(SET_EDIT_FORM, false);
    },
    removeImageAsset() {
      this.startProcessing();
      const params = {
        accountId: this.getCurrentAccountId,
        assetId: this.source.id,
      };

      this.$store.dispatch(DELETE_IMAGE_ASSET_REQUEST, params).then(() => {
        this.$toasted.global.success({
          message: 'The Image asset has been successfully deleted',
        });
        this.stopProcessing();
        this.getMediaItemAssets(this.mediaItem.id);
      });
    },
    onRevertChangesHandle() {
      this.formData.language = this.cachedFormData.language;
      this.formData.assetRole = this.cachedFormData.assetRole;
    },
    onEditImageAsset() {
      this.startProcessing();
      const params = {
        accountId: this.getCurrentAccountId,
        audioId: this.source.id,
        data: {
          language: this.formData.language,
          assetRole: this.formData.assetRole === 'No-items' ? '' : this.formData.assetRole,
        },
      };
      this.$store.dispatch(EDIT_IMAGE_ASSET_REQUEST, params)
        .then(() => {
          this.$toasted.global.success({
            message: 'The Image asset has been successfully updated',
          });
          this.cachedFormData.assetRole = this.formData.assetRole;
          this.cachedFormData.language = this.formData.language;

          this.getMediaItemAssets(this.mediaItem.id);
          this.stopProcessing();
        })
        .catch((error) => {
          this.onSubmitFail(error);
        });
    },
    onSubmitFail(error) {
      const formErrors = this.getServerFormErrors(error);

      const formatError = error
        && error.form_errors
        && error.form_errors.children
        && error.form_errors.children.assetRole
        && error.form_errors.children.assetRole.errors
        && error.form_errors.children.assetRole.errors[0].error;

      if (formatError === 'INVALID_AUDIO_FORMAT') {
        this.formatError = 'File format is invalid. Supported audio formats for the Podcast Audio: MP3, AAC';
      }

      if (Object.entries(formErrors).length && this.$refs[this.validationObserver]) {
        this.$refs[this.validationObserver].setErrors(formErrors);
      } else {
        this.$toasted.global.error({
          message: this.errorMessage,
        });
      }
    },
  },
};
</script>

<style scoped>
.image-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 614px;
  position: relative;
}
.info-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
}
.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
}
.item-title {
  color: rgb(130, 130, 130);
}
.item-description {
  color: rgb(255, 255, 255);
}
.btn-wrapper {
  display: flex;
  flex-direction: row;
  gap: 15px;
  margin-top: 10px;
}
</style>
